import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RoleEntity } from '@magimp/shared';
import { useRole } from '@magimp/shared-hooks';
import {
  EditorDrawer,
  DetailSheet,
  StatusBadge,
  LoadingState,
  ErrorState,
  Tabs,
  useEditorGuard,
} from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { RoleDetailsTab } from './RoleDetailsTab.js';
import { RolePermissionsTab } from './RolePermissionsTab.js';
import { RoleUsersTab } from './RoleUsersTab.js';

// WHY: 'logist' is kept alongside 'logist_cn' so the UI does not show the
// archive control for a system-role row that arrives over the wire still
// labelled with the legacy name (e.g. during rolling deploy or a snapshot
// restore predating the rename migration). 'logist_ru' (#623) is the RF
// parallel of logist_cn and is equally a protected system role. 'executive'
// (#1591) is the leadership role and is archive-blocked like the others.
// 'financier_ru' (#1935) is the RF client-payment reviewer — archive-blocked
// like the others but NOT permission-locked (an operational role), so it is
// absent from PERMISSION_LOCKED_ROLE_NAMES below.
// 'fin_director' (#2565) follows the same archive-blocked, permission-editable
// contract so the founder can extend its grants later.
const SYSTEM_ARCHIVE_BLOCKED = new Set([
  'admin',
  'manager',
  'logist',
  'logist_cn',
  'logist_ru',
  'executive',
  'financier_ru',
  'fin_director',
]);

// WHY (#1591): the NARROW set of system roles whose details + permission grid
// are fully read-only, mirroring the backend `PERMISSION_LOCKED_ROLE_NAMES`
// guard in `roles.service.ts`. Only `admin` and `executive` are locked here;
// operational system roles (manager/logist*/rop/financier_ru/fin_director)
// stay editable.
const PERMISSION_LOCKED_ROLE_NAMES = new Set(['admin', 'executive']);

export interface RoleEditDrawerProps {
  id: string;
  onClose: () => void;
}

export function RoleEditDrawer({ id, onClose }: RoleEditDrawerProps) {
  const { t } = useTranslation('common');
  const roleQ = useRole(id);
  // Dirty/busy lifted out of the per-tab components so the drawer can wire
  // ONE shared guard that covers Drawer Escape/backdrop, DetailSheet back
  // button, and dirty tab switches. Each editable tab (details, permissions)
  // bubbles its own dirty/busy via narrow primitive callbacks; the read-only
  // Users tab does not contribute.
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <EditorDrawer width={620} dirty={dirty} busy={busy} onConfirmClose={onClose}>
      {roleQ.isPending && <LoadingState />}
      {roleQ.error && <ErrorState title={t('states.loadError')} text={errorMessage(roleQ.error)} />}
      {roleQ.isSuccess && (
        <LoadedDrawer
          role={roleQ.data}
          onClose={onClose}
          onDirtyChange={setDirty}
          onBusyChange={setBusy}
        />
      )}
    </EditorDrawer>
  );
}

type Tab = 'details' | 'permissions' | 'users';

interface LoadedDrawerProps {
  role: RoleEntity;
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onBusyChange: (busy: boolean) => void;
}

function LoadedDrawer({ role, onClose, onDirtyChange, onBusyChange }: LoadedDrawerProps) {
  const { t } = useTranslation(['roles', 'common']);
  // WHY: `confirmDestructive` is needed for the dirty tab-switch guard
  // (details ↔ permissions ↔ users) and for the DetailSheet back button.
  // Tab switches unmount the active form; routing them through the editor
  // guard surfaces the discard prompt so typed-but-unsaved input is not
  // silently lost.
  const { confirmDestructive } = useEditorGuard();
  const [tab, setTab] = useState<Tab>('details');
  // #1591: details + permission grid are read-only for permission-locked
  // system roles (admin + executive), not admin-only.
  const permissionLocked = role.isSystem && PERMISSION_LOCKED_ROLE_NAMES.has(role.name);
  const archiveBlocked = role.isSystem && SYSTEM_ARCHIVE_BLOCKED.has(role.name);

  // Dirty-tab-switch policy (issue #628 spec §19): the active tab is the
  // only contributor to dirty/busy. Switching away from a dirty tab must
  // route through the shared guard so the user is offered the prompt; if
  // they continue editing we stay on the current tab; if they discard, the
  // tab's local state is dropped on unmount and we switch.
  const requestTab = (next: Tab) => {
    if (next === tab) return;
    confirmDestructive(() => setTab(next));
  };

  return (
    <DetailSheet
      title={role.displayName}
      onClose={() => confirmDestructive(onClose)}
      right={
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {role.isSystem && <StatusBadge status="system" />}
          <StatusBadge status={role.archived ? 'archived' : 'activeFeminine'} />
        </div>
      }
    >
      <Tabs<Tab>
        tabs={[
          { id: 'details', label: t('roles:tabs.details') },
          { id: 'permissions', label: t('roles:tabs.permissions') },
          { id: 'users', label: t('roles:tabs.users') },
        ]}
        value={tab}
        onChange={requestTab}
      />
      {tab === 'details' && (
        <RoleDetailsTab
          role={role}
          disabled={permissionLocked}
          archiveBlocked={archiveBlocked}
          onDirtyChange={onDirtyChange}
          onBusyChange={onBusyChange}
        />
      )}
      {tab === 'permissions' && (
        <RolePermissionsTab
          role={role}
          disabled={permissionLocked}
          onDirtyChange={onDirtyChange}
          onBusyChange={onBusyChange}
        />
      )}
      {tab === 'users' && <RoleUsersTab roleId={role.id} />}
    </DetailSheet>
  );
}

export {
  RoleArchiveConfirm,
  RoleRestoreConfirm,
  type RoleArchiveConfirmProps,
  type RoleRestoreConfirmProps,
} from './RoleArchiveRestoreConfirm.js';
